import { useMutation } from "@apollo/client";
import classNames from "classnames/bind";
import { forwardRef } from "react";
import { DndProvider, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Directory } from "../../../../../gql/graphql";
import { DIRECTORY_UPDATE } from "../../../../../graphql/mutations/FolderMutations";
import { FolderGroup } from "./FolderGroup/FolderGroup";

import styles from "./FoldersList.module.scss";

const cx = classNames.bind(styles);

export type Folder = Pick<Directory, "id" | "name" | "parentDirectory" | "default"> & {
  children?: Folder[];
};

export interface FoldersListProps {
  groupedFolders?: Folder[];
  isEditable?: boolean;
  selected?: Folder;
  setSelected?: (folder: Folder) => void;
  className?: string;
}

export const SideDrop = forwardRef<HTMLDivElement, { show: boolean }>(({ show }, ref) => {
  if (show) {
    return <div ref={ref} className={styles.sideDrop}></div>;
  }

  return null;
});
SideDrop.displayName = "SideDrop";

export const FoldersListDnd = ({ groupedFolders, selected, setSelected, isEditable, className }: FoldersListProps) => {
  const [updateDirectory] = useMutation(DIRECTORY_UPDATE);
  const [{ showSideDrop, isOverSideDrop, currentlyMovingFolder }, drop] = useDrop(() => ({
    accept: "FOLDER",
    canDrop: () => true,
    drop: (item: Folder) => {
      updateDirectory({
        variables: {
          directoryId: item.id,
          data: {
            parentDirectoryId: null,
          },
        },
      });
    },
    collect: (monitor) => ({
      showSideDrop: monitor.canDrop(),
      isOverSideDrop: monitor.isOver({ shallow: true }),
      currentlyMovingFolder: monitor.getItem<Folder>(),
    }),
  }));

  const foldersWithVirtual =
    groupedFolders && isOverSideDrop
      ? [
          ...groupedFolders.slice(0, 1),
          { ...currentlyMovingFolder, virtual: true, id: "virtual" },
          ...groupedFolders.slice(1),
        ]
      : groupedFolders;

  return (
    <div className={cx("foldersList", className)}>
      <SideDrop show={showSideDrop} ref={drop} />

      {foldersWithVirtual?.map((folder) => {
        return (
          <FolderGroup
            key={folder.id}
            folder={folder}
            selected={selected}
            setSelected={setSelected}
            isEditable={isEditable}
          />
        );
      })}
    </div>
  );
};

export const FoldersList = ({ groupedFolders, selected, setSelected, isEditable, className }: FoldersListProps) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <FoldersListDnd
        groupedFolders={groupedFolders}
        selected={selected}
        setSelected={setSelected}
        isEditable={isEditable}
        className={className}
      />
    </DndProvider>
  );
};
